import React from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { useColorMode } from '../../colorMode';
import { GOOGLE_MAPS_VECTOR_MAP_ID, GOOGLE_MAPS_VECTOR_MAP_ID_DARK, GOOGLE_MAPS_VECTOR_MAP_ID_LIGHT } from './googleMapConfig';
import { AdvancedMarkerSupportProvider } from './AdvancedMarkerSupportContext';
import useMap from './useMap';
import Map from './NativeMap';
function GoogleMapComponent({ apiKey, draggable, gestureHandling, onClick, children }) {
    const { center, setCenter, setMap, zoom, setZoom, mapTypeId } = useMap();
    const { isThemeDark } = useColorMode();
    // Apply dark mode via CSS color-scheme override
    React.useEffect(() => {
        const htmlElement = document.documentElement;
        if (isThemeDark) {
            htmlElement.style.colorScheme = 'dark';
        }
        else {
            htmlElement.style.colorScheme = 'light';
        }
    }, [isThemeDark]);
    const render = React.useCallback((status) => {
        if (status === Status.LOADING) {
            return React.createElement("div", { style: { padding: 8 } }, "Loading map...");
        }
        if (status === Status.FAILURE) {
            return (React.createElement("div", { style: { padding: 8 } },
                React.createElement("div", null, "Failed to load Google Maps script."),
                React.createElement("div", { style: { marginTop: 8, fontSize: 12, lineHeight: 1.4 } },
                    "Check ",
                    React.createElement("code", null, "GOOGLE_MAP_API_KEY"),
                    " (billing, HTTP referrer restrictions) and that the Maps JavaScript API is enabled. Advanced markers require valid vector Map IDs: set ",
                    React.createElement("code", null, "GOOGLE_MAPS_VECTOR_MAP_ID"),
                    " (and light/dark variants for roadmap) in the website build environment; without them the UI falls back to classic markers.")));
        }
        return React.createElement(React.Fragment, null);
    }, []);
    const handleClick = React.useCallback((e) => {
        if (e.latLng) {
            onClick?.(e.latLng.toJSON());
        }
    }, [onClick]);
    const onIdle = React.useCallback((map) => {
        setMap?.(map);
        const tmpZoom = map.getZoom();
        if (tmpZoom) {
            setZoom?.(tmpZoom);
        }
        const tmpCenter = map.getCenter();
        if (tmpCenter) {
            setCenter?.(tmpCenter.toJSON());
        }
    }, [setMap, setCenter, setZoom]);
    if (!center) {
        return React.createElement(React.Fragment, null, "Loading...");
    }
    const trimmedKey = apiKey?.trim() ?? '';
    if (!trimmedKey) {
        return (React.createElement("div", { style: { flexGrow: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 } }, "Google Maps API key is not configured (set GOOGLE_MAP_API_KEY in repo-root .env and rebuild member-website)."));
    }
    const currentMapTypeId = mapTypeId || 'roadmap';
    const activeMapId = currentMapTypeId === 'hybrid'
        ? GOOGLE_MAPS_VECTOR_MAP_ID
        : (isThemeDark ? GOOGLE_MAPS_VECTOR_MAP_ID_DARK : GOOGLE_MAPS_VECTOR_MAP_ID_LIGHT) || GOOGLE_MAPS_VECTOR_MAP_ID;
    // When mapId is set (required for AdvancedMarkerElement), programmatic `styles` are not allowed — use Cloud Console map styling.
    const useVectorMapId = activeMapId && activeMapId !== 'DEMO_MAP_ID';
    const colorSchemeOption = currentMapTypeId === 'hybrid'
        ? {}
        : {
            // Per Maps JS API docs, colorScheme only applies when map is initialized.
            colorScheme: isThemeDark ? 'DARK' : 'LIGHT',
        };
    const baseMapOptions = {
        draggable,
        ...(gestureHandling ? { gestureHandling } : {}),
        center,
        zoom,
        ...(useVectorMapId ? { mapId: activeMapId } : {}),
        mapTypeId: currentMapTypeId,
        backgroundColor: 'transparent',
        disableDoubleClickZoom: true,
        disableDefaultUI: true,
        keyboardShortcuts: false,
        rotateControl: false,
        tilt: 0,
        ...colorSchemeOption,
    };
    return (React.createElement(Wrapper, { apiKey: trimmedKey, libraries: ['marker'], render: render },
        React.createElement(AdvancedMarkerSupportProvider, { value: Boolean(useVectorMapId) },
            React.createElement(Map, { key: `${currentMapTypeId}:${currentMapTypeId === 'hybrid' ? 'fixed' : isThemeDark ? 'dark' : 'light'}:${activeMapId || 'none'}`, ...baseMapOptions, onIdle: onIdle, onClick: onClick ? handleClick : undefined, style: { flexGrow: '1', height: '100%' } }, children))));
}
export default React.memo(GoogleMapComponent);
